// Sound Effects Utility using Web Audio API
// Lightweight synthesized sounds - no external files needed

class SoundEffects {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.volume = 0.3; // Default volume (0-1)
    this.musicEnabled = false;
    this.musicVolume = 0.15;
    this.musicOscillators = [];
    this.musicGainNode = null;
    this.musicLoopTimeout = null;
  }

  // Lazy initialization of AudioContext (required for browser autoplay policies)
  getContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    return this.audioContext;
  }

  // Play a note with given frequency and duration
  playTone(frequency, duration, type = 'sine', volumeMultiplier = 1) {
    if (!this.enabled) return;
    
    try {
      const ctx = this.getContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      
      // Envelope: quick attack, sustain, quick release
      const vol = this.volume * volumeMultiplier;
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('Sound effect failed:', e);
    }
  }

  // Play a sequence of notes
  playSequence(notes, baseDelay = 0) {
    if (!this.enabled) return;
    
    notes.forEach(({ frequency, duration, delay = 0, type = 'sine', volume = 1 }) => {
      setTimeout(() => {
        this.playTone(frequency, duration, type, volume);
      }, (baseDelay + delay) * 1000);
    });
  }

  // ============================================
  // BACKGROUND MUSIC - Soothing Piano Melody
  // ============================================
  
  // Start soothing piano background melody
  startBackgroundMusic() {
    if (!this.musicEnabled) return;
    
    try {
      const ctx = this.getContext();
      
      // Stop any existing music
      this.stopBackgroundMusic();
      
      // Create master gain for music
      this.musicGainNode = ctx.createGain();
      this.musicGainNode.connect(ctx.destination);
      this.musicGainNode.gain.setValueAtTime(0, ctx.currentTime);
      this.musicGainNode.gain.linearRampToValueAtTime(this.musicVolume, ctx.currentTime + 2);
      
      // Piano-like melody - gentle arpeggiated chords (C major / A minor progressions)
      // Emulates a soft, contemplative piano piece
      const melodyPatterns = [
        // C major arpeggio
        { notes: [261.63, 329.63, 392.00, 523.25], duration: 0.5 },  // C4, E4, G4, C5
        { notes: [329.63, 392.00, 523.25], duration: 0.4 },         // E4, G4, C5
        { notes: [392.00, 523.25, 659.25], duration: 0.6 },         // G4, C5, E5
        { notes: [0], duration: 0.3 },                               // Rest
        
        // A minor arpeggio  
        { notes: [220.00, 261.63, 329.63, 440.00], duration: 0.5 }, // A3, C4, E4, A4
        { notes: [261.63, 329.63, 440.00], duration: 0.4 },         // C4, E4, A4
        { notes: [329.63, 440.00, 523.25], duration: 0.6 },         // E4, A4, C5
        { notes: [0], duration: 0.3 },                               // Rest
        
        // F major arpeggio
        { notes: [174.61, 261.63, 349.23, 440.00], duration: 0.5 }, // F3, C4, F4, A4
        { notes: [261.63, 349.23, 523.25], duration: 0.4 },         // C4, F4, C5
        { notes: [0], duration: 0.2 },                               // Rest
        
        // G major resolution
        { notes: [196.00, 246.94, 392.00, 587.33], duration: 0.6 }, // G3, B3, G4, D5
        { notes: [246.94, 392.00, 493.88], duration: 0.5 },         // B3, G4, B4
        { notes: [392.00, 493.88, 587.33], duration: 0.8 },         // G4, B4, D5
        { notes: [0], duration: 0.5 },                               // Rest
      ];
      
      // Calculate total loop duration
      const totalDuration = melodyPatterns.reduce((sum, p) => sum + p.duration, 0);
      
      // Function to play a piano-like note
      const playPianoNote = (freq, time, duration) => {
        if (freq <= 0) return;
        
        // Piano uses multiple harmonics for richer tone
        const harmonics = [1, 2, 3, 4];
        const harmonicVolumes = [0.6, 0.2, 0.1, 0.05];
        
        harmonics.forEach((harmonic, i) => {
          const osc = ctx.createOscillator();
          const noteGain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          // Use triangle for softer piano-like tone
          osc.type = i === 0 ? 'triangle' : 'sine';
          osc.frequency.setValueAtTime(freq * harmonic, time);
          
          // Low-pass filter for warmth
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(2000 - (harmonic * 200), time);
          filter.Q.setValueAtTime(0.5, time);
          
          osc.connect(filter);
          filter.connect(noteGain);
          noteGain.connect(this.musicGainNode);
          
          // Piano envelope: quick attack, long decay
          const vol = harmonicVolumes[i] * 0.25;
          noteGain.gain.setValueAtTime(0, time);
          noteGain.gain.linearRampToValueAtTime(vol, time + 0.02); // Quick attack
          noteGain.gain.exponentialRampToValueAtTime(vol * 0.6, time + 0.1); // Initial decay
          noteGain.gain.exponentialRampToValueAtTime(0.001, time + duration + 0.3); // Sustain and release
          
          osc.start(time);
          osc.stop(time + duration + 0.5);
        });
      };
      
      // Play melody in a loop
      const playMelody = () => {
        if (!this.musicEnabled) return;
        
        let time = ctx.currentTime;
        
        melodyPatterns.forEach(({ notes, duration }) => {
          // Arpeggiate the notes slightly for piano feel
          notes.forEach((note, noteIndex) => {
            if (note > 0) {
              playPianoNote(note, time + (noteIndex * 0.08), duration);
            }
          });
          time += duration;
        });
        
        // Schedule next loop
        this.musicLoopTimeout = setTimeout(playMelody, totalDuration * 1000);
      };
      
      playMelody();
      
    } catch (e) {
      console.warn('Background music failed:', e);
    }
  }
  
  // Stop background music
  stopBackgroundMusic() {
    if (this.musicLoopTimeout) {
      clearTimeout(this.musicLoopTimeout);
      this.musicLoopTimeout = null;
    }
    
    if (this.musicGainNode) {
      try {
        const ctx = this.getContext();
        this.musicGainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
      } catch (e) {}
    }
    
    this.musicOscillators.forEach(({ osc, vibrato }) => {
      try {
        if (osc) osc.stop();
        if (vibrato) vibrato.stop();
      } catch (e) {}
    });
    this.musicOscillators = [];
  }
  
  // Toggle background music
  toggleMusic() {
    this.musicEnabled = !this.musicEnabled;
    if (this.musicEnabled) {
      this.startBackgroundMusic();
    } else {
      this.stopBackgroundMusic();
    }
    return this.musicEnabled;
  }
  
  // Set music volume
  setMusicVolume(vol) {
    this.musicVolume = Math.max(0, Math.min(1, vol));
    if (this.musicGainNode) {
      try {
        const ctx = this.getContext();
        this.musicGainNode.gain.linearRampToValueAtTime(this.musicVolume, ctx.currentTime + 0.5);
      } catch (e) {}
    }
  }

  // ============================================
  // PRESET SOUNDS
  // ============================================

  // Correct answer - cheerful ascending chime
  playCorrect() {
    this.playSequence([
      { frequency: 523.25, duration: 0.1, delay: 0, type: 'sine' },      // C5
      { frequency: 659.25, duration: 0.1, delay: 0.08, type: 'sine' },   // E5
      { frequency: 783.99, duration: 0.2, delay: 0.16, type: 'sine' },   // G5
    ]);
  }

  // Wrong answer - soft descending tone
  playIncorrect() {
    this.playSequence([
      { frequency: 311.13, duration: 0.15, delay: 0, type: 'triangle', volume: 0.6 },    // Eb4
      { frequency: 277.18, duration: 0.2, delay: 0.12, type: 'triangle', volume: 0.5 },  // Db4
    ]);
  }

  // Level up - triumphant fanfare
  playLevelUp() {
    this.playSequence([
      { frequency: 523.25, duration: 0.12, delay: 0, type: 'square', volume: 0.4 },      // C5
      { frequency: 659.25, duration: 0.12, delay: 0.1, type: 'square', volume: 0.4 },    // E5
      { frequency: 783.99, duration: 0.12, delay: 0.2, type: 'square', volume: 0.4 },    // G5
      { frequency: 1046.50, duration: 0.3, delay: 0.3, type: 'sine', volume: 0.6 },      // C6
      { frequency: 783.99, duration: 0.15, delay: 0.5, type: 'sine', volume: 0.3 },      // G5
      { frequency: 1046.50, duration: 0.4, delay: 0.6, type: 'sine', volume: 0.5 },      // C6
    ]);
  }

  // XP earned - coin collect sound
  playXP() {
    this.playSequence([
      { frequency: 987.77, duration: 0.08, delay: 0, type: 'square', volume: 0.3 },      // B5
      { frequency: 1318.51, duration: 0.15, delay: 0.06, type: 'square', volume: 0.4 },  // E6
    ]);
  }

  // Achievement unlocked - special celebration
  playAchievement() {
    this.playSequence([
      { frequency: 392.00, duration: 0.1, delay: 0, type: 'sine' },       // G4
      { frequency: 493.88, duration: 0.1, delay: 0.08, type: 'sine' },    // B4
      { frequency: 587.33, duration: 0.1, delay: 0.16, type: 'sine' },    // D5
      { frequency: 783.99, duration: 0.25, delay: 0.24, type: 'sine' },   // G5
      { frequency: 987.77, duration: 0.3, delay: 0.4, type: 'sine', volume: 0.8 },  // B5
    ]);
  }

  // Button click - subtle tap
  playClick() {
    this.playTone(800, 0.05, 'sine', 0.2);
  }

  // Session start - welcome chime
  playSessionStart() {
    this.playSequence([
      { frequency: 440, duration: 0.15, delay: 0, type: 'sine', volume: 0.5 },          // A4
      { frequency: 554.37, duration: 0.15, delay: 0.12, type: 'sine', volume: 0.5 },    // C#5
      { frequency: 659.25, duration: 0.25, delay: 0.24, type: 'sine', volume: 0.6 },    // E5
    ]);
  }

  // Session end - gentle goodbye
  playSessionEnd() {
    this.playSequence([
      { frequency: 659.25, duration: 0.15, delay: 0, type: 'sine', volume: 0.4 },       // E5
      { frequency: 554.37, duration: 0.15, delay: 0.12, type: 'sine', volume: 0.4 },    // C#5
      { frequency: 440, duration: 0.3, delay: 0.24, type: 'sine', volume: 0.5 },        // A4
    ]);
  }

  // Streak milestone - fire crackle effect
  playStreakMilestone() {
    this.playSequence([
      { frequency: 200, duration: 0.05, delay: 0, type: 'sawtooth', volume: 0.2 },
      { frequency: 250, duration: 0.05, delay: 0.05, type: 'sawtooth', volume: 0.25 },
      { frequency: 300, duration: 0.05, delay: 0.1, type: 'sawtooth', volume: 0.3 },
      { frequency: 400, duration: 0.1, delay: 0.15, type: 'sine', volume: 0.4 },
      { frequency: 600, duration: 0.15, delay: 0.2, type: 'sine', volume: 0.5 },
      { frequency: 800, duration: 0.2, delay: 0.3, type: 'sine', volume: 0.4 },
    ]);
  }

  // Notification - gentle ping
  playNotification() {
    this.playSequence([
      { frequency: 880, duration: 0.1, delay: 0, type: 'sine', volume: 0.4 },           // A5
      { frequency: 1108.73, duration: 0.15, delay: 0.08, type: 'sine', volume: 0.3 },   // C#6
    ]);
  }

  // Error - soft warning
  playError() {
    this.playSequence([
      { frequency: 200, duration: 0.15, delay: 0, type: 'triangle', volume: 0.4 },
      { frequency: 180, duration: 0.2, delay: 0.1, type: 'triangle', volume: 0.3 },
    ]);
  }

  // Toggle sounds on/off
  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  // Set volume (0-1)
  setVolume(vol) {
    this.volume = Math.max(0, Math.min(1, vol));
  }
}

// ============================================
// HAPTIC FEEDBACK
// ============================================

class HapticFeedback {
  constructor() {
    this.enabled = true;
    this.supported = 'vibrate' in navigator;
  }
  
  // Light tap - for button presses
  tap() {
    if (!this.enabled || !this.supported) return;
    navigator.vibrate(10);
  }
  
  // Success - for correct answers
  success() {
    if (!this.enabled || !this.supported) return;
    navigator.vibrate([30, 50, 30]); // Two quick pulses
  }
  
  // Error - for wrong answers
  error() {
    if (!this.enabled || !this.supported) return;
    navigator.vibrate([100, 30, 100]); // Two longer pulses
  }
  
  // Achievement - celebration pattern
  achievement() {
    if (!this.enabled || !this.supported) return;
    navigator.vibrate([50, 50, 50, 50, 100]); // Ascending pattern
  }
  
  // Level up - strong celebration
  levelUp() {
    if (!this.enabled || !this.supported) return;
    navigator.vibrate([100, 50, 100, 50, 200]); // Building to climax
  }
  
  // Toggle haptics
  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }
  
  // Check if supported
  isSupported() {
    return this.supported;
  }
}

// Singleton instances
const soundEffects = new SoundEffects();
const hapticFeedback = new HapticFeedback();

// Export individual functions for easy use
export const playCorrect = () => soundEffects.playCorrect();
export const playIncorrect = () => soundEffects.playIncorrect();
export const playLevelUp = () => soundEffects.playLevelUp();
export const playXP = () => soundEffects.playXP();
export const playAchievement = () => soundEffects.playAchievement();
export const playClick = () => soundEffects.playClick();
export const playSessionStart = () => soundEffects.playSessionStart();
export const playSessionEnd = () => soundEffects.playSessionEnd();
export const playStreakMilestone = () => soundEffects.playStreakMilestone();
export const playNotification = () => soundEffects.playNotification();
export const playError = () => soundEffects.playError();

// Export control functions
export const toggleSound = () => soundEffects.toggle();
export const setSoundVolume = (vol) => soundEffects.setVolume(vol);
export const isSoundEnabled = () => soundEffects.enabled;

// Background music controls
export const toggleMusic = () => soundEffects.toggleMusic();
export const startMusic = () => soundEffects.startBackgroundMusic();
export const stopMusic = () => soundEffects.stopBackgroundMusic();
export const setMusicVolume = (vol) => soundEffects.setMusicVolume(vol);
export const isMusicEnabled = () => soundEffects.musicEnabled;

// Haptic feedback controls
export const hapticTap = () => hapticFeedback.tap();
export const hapticSuccess = () => hapticFeedback.success();
export const hapticError = () => hapticFeedback.error();
export const hapticAchievement = () => hapticFeedback.achievement();
export const hapticLevelUp = () => hapticFeedback.levelUp();
export const toggleHaptics = () => hapticFeedback.toggle();
export const isHapticsSupported = () => hapticFeedback.isSupported();
export const isHapticsEnabled = () => hapticFeedback.enabled;

// Export the class for advanced usage
export default soundEffects;
